import { FULL_HD, HD, safetyMargins, RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import { DEFAULT_MODE, makeThemeColor, PRIMARY } from '@tornado-rdk/theme';
import { FONT, getFont } from '@tornado-rdk/typography';
import { makeDefaultStylingContext } from '../utils/style';
import { SCREEN_WIDTH } from '../utils/sizes';
import { WIDTH as SIDEBAR_WIDTH } from '../../sidenav/sidenav.constants';
import { imageOptions, templates, VERTICAL_PADDING } from './jumbotronSquare.constants';

export const defaultContext = makeDefaultStylingContext();

export const colors = {
  text: makeThemeColor(PRIMARY),
  separator: makeThemeColor(PRIMARY, 0.3),
};

export const IMAGE_RADIUS = {
  [HD]: 4,
  [FULL_HD]: 8,
};

export const SEPARATOR_HEIGHT = {
  [HD]: 2,
  [FULL_HD]: 4,
};

export const CONTAINER_A_GAP = {
  [HD]: 48,
  [FULL_HD]: 48,
};

export const CONTAINER_D_GAP = {
  [HD]: 32,
  [FULL_HD]: 32,
};

export const CONTAINER_E_GAP = {
  [HD]: 8,
  [FULL_HD]: 8,
};

export const CONTAINER_H_GAP = {
  [HD]: 24,
  [FULL_HD]: 24,
};

export const CONTAINER_I_GAP = {
  [HD]: 8,
  [FULL_HD]: 8,
};

export const BUTTONS_CONTAINER_GAP = {
  [HD]: 16,
  [FULL_HD]: 24,
};

export const INFO_ICON = { name: 'InfoLight', type: 'icon' };

export const getLeftContainerWidth = (screenSize, offset) =>
  SCREEN_WIDTH[screenSize] -
  (offset || safetyMargins.horizontal[screenSize]) -
  imageOptions[templates.JumbotronSquare][RENDERING_CONTEXT.GENERAL][screenSize].PRIMARY.width -
  CONTAINER_A_GAP[screenSize] -
  safetyMargins.horizontal[screenSize];

export const makeBasicTextStyle = ({ font, colorMode = DEFAULT_MODE, maxLines = 2 }) => ({
  color: colors.text[colorMode],
  text: {
    ...font,
    verticalAlign: 'middle',
    wordWrap: true,
    maxLines,
    maxLinesSuffix: '...',
  },
});

export const getInitialStyle = ({ screenSize = FULL_HD, colorMode = DEFAULT_MODE } = defaultContext) => {
  const imageSize = imageOptions[templates.JumbotronSquare][RENDERING_CONTEXT.GENERAL][screenSize].PRIMARY.width;

  return {
    FlexContainer: {
      // NOTE: same as in the AbstractLayout
      x: SIDEBAR_WIDTH[screenSize] + 40,
      flex: {
        direction: 'row',
        paddingTop: VERTICAL_PADDING[screenSize],
      },
      Image: {
        w: imageSize,
        h: imageSize,
        shader: {
          radius: IMAGE_RADIUS[screenSize],
        },
        flexItem: {
          marginRight: CONTAINER_A_GAP[screenSize],
        },
      },
      ContainerD: {
        flex: {
          direction: 'column',
        },
        ContainerE: {
          flex: {
            direction: 'column',
          },
          flexItem: {
            marginBottom: CONTAINER_D_GAP[screenSize],
          },
          Incentive: {
            flexItem: {
              marginBottom: CONTAINER_E_GAP[screenSize],
            },
          },
          Title: {
            flexItem: {
              marginBottom: CONTAINER_E_GAP[screenSize],
            },
            ...makeBasicTextStyle({
              font: getFont(FONT.PARIS_3, screenSize),
              colorMode,
            }),
          },
          Author: {
            flexItem: {
              marginBottom: CONTAINER_E_GAP[screenSize],
            },
            alpha: 0.6,
            ...makeBasicTextStyle({
              font: getFont(FONT.OTTAWA_1, screenSize),
              colorMode,
            }),
          },
        },
        ContainerH: {
          flex: {
            direction: 'column',
          },
          flexItem: {
            marginBottom: CONTAINER_D_GAP[screenSize],
          },
          ProgressBar: {
            flexItem: {
              marginBottom: CONTAINER_H_GAP[screenSize],
            },
          },
          ButtonsContainer: {
            flex: {
              direction: 'row',
            },
            PlayButton: {
              flexItem: {
                marginRight: BUTTONS_CONTAINER_GAP[screenSize],
              },
            },
            MyListButton: {
              flexItem: {
                marginRight: BUTTONS_CONTAINER_GAP[screenSize],
              },
            },
            InfoButton: {
              picto: INFO_ICON,
            },
          },
        },
        Separator: {
          flexItem: {
            marginBottom: CONTAINER_D_GAP[screenSize],
          },
          rect: true,
          color: colors.separator[colorMode],
          h: SEPARATOR_HEIGHT[screenSize],
        },
        ContainerI: {
          flex: {
            direction: 'column',
          },
          ExtraDetails: {
            flexItem: {
              marginBottom: CONTAINER_I_GAP[screenSize],
            },
            alpha: 0.6,
            ...makeBasicTextStyle({
              font: getFont(FONT.MADRID_3, screenSize),
              colorMode,
            }),
          },
          Description: {
            ...makeBasicTextStyle({
              font: getFont(FONT.OTTAWA_1, screenSize),
              colorMode,
              maxLines: 3,
            }),
          },
        },
      },
    },
  };
};
